import type { Socket } from 'socket.io-client';
import { dashboardApi } from '@/store/api/base/api.base';
import { SERVICE_ENDPOINTS_TAG, begatewayEndpointsApi } from '@/store/api/begateway/begateway.endpoints.api';
import { dropEndpoint, writeEndpoint } from '@/store/api/begateway/begateway.endpoints.cache';
import customerApi from '@/store/api/customer/customer.api';
import catalogApi from '@/store/api/catalog/catalog.api';
import { dropBundle, dropItem, writeBundle, writeItem } from '@/store/api/catalog/catalog.cache';
import { dropEvent, writeEvent } from '@/store/api/calendar/calendar.event.cache';
import { dropExternalCalendar, writeExternalCalendar } from '@/store/api/calendar/calendar.external.cache';
import { writeDayNote } from '@/store/api/calendar/calendar.daynote.cache';
import expenseApi from '@/store/api/expense/expense.api';
import { dropExpense, writeExpense } from '@/store/api/expense/expense.cache';
import invoiceApi from '@/store/api/commercial/invoice.api';
import quoteApi from '@/store/api/commercial/quote.api';
import companyApi from '@/store/api/company/company.api';
import { writeCompany } from '@/store/api/company/company.cache';
import { writeMail } from '@/store/api/mail/mail.cache';
import {
    dropConversation,
    dropConversationContact,
    dropConversationMessage,
    writeConversation,
    writeConversationContact,
    writeConversationMessage,
} from '@/store/api/conversation/email/conversation.cache';
import {
    applyChatRead,
    dropChatMessage,
    writeChatArchive,
    writeChatMessage,
    writeChatMute,
} from '@/store/api/conversation/chat/chat.cache';
import { emitChatIncoming } from '@/lib/chat/chat.incoming.bus';
import { applyChatPresence, removeChatChannel, upsertChatChannel } from '@/store/api/conversation/chat/chat.group.cache';
import { dropChatPinned, syncChatPinned } from '@/store/api/conversation/chat/chat.manage.cache';
import { chatTypingSeen, chatTypingStop } from '@/lib/chat/chat.typing.store';
import type { TypeSessionUser } from '@/types/auth/types.auth.session';
import type { TypeAccessRule } from '@/types/begateway/types.begateway.access';
import type { TypeDiagnoseRun } from '@/types/begateway/types.begateway.diagnose';
import type { TypeGatewayEndpoint } from '@/types/begateway/types.begateway.endpoint';
import type { TypeApiKey } from '@/types/begateway/types.begateway.key';
import type { TypeRoute } from '@/types/begateway/types.begateway.route';
import type { TypeGatewayService } from '@/types/begateway/types.begateway.service';
import type { TypeDayNote, TypeEvent } from '@/types/calendar/types.calendar';
import type { TypeExternalCalendar } from '@/types/calendar/types.calendar.external';
import type { TypeCatalogBundle, TypeCatalogItem } from '@/types/catalog/types.catalog';
import type { TypeInvoice } from '@/types/commercial/types.commercial.invoice';
import type { TypeQuote } from '@/types/commercial/types.commercial.quote';
import type { TypeCompany } from '@/types/company/types.company';
import type { TypeChatChannelUpsert, TypeChatPresenceUpdate } from '@/types/conversation/types.conversation.chat.channel';
import type { TypeChatMessage } from '@/types/conversation/types.conversation.chat.message';
import type { TypeConversation, TypeConversationMessage } from '@/types/conversation/types.conversation.email';
import type { TypeConversationContact } from '@/types/conversation/types.conversation.email.contact';
import type { TypeConversationDraft, TypeConversationTemplate } from '@/types/conversation/types.conversation.email.draft';
import type { TypeCustomer } from '@/types/customer/types.customer';
import type { TypeExpense } from '@/types/expense/types.expense';
import type { TypeMail } from '@/types/mail/types.mail';
import type { TypeNotification } from '@/types/notification/types.notification';
import type { TypeOpsActionRun, TypeOpsRun } from '@/types/ops/types.ops.action';
import type { TypeOpsActivity } from '@/types/ops/types.ops.activity';
import type { TypeOpsAlert } from '@/types/ops/types.ops.alert';
import type { TypeOpsDeploy } from '@/types/ops/types.ops.deploy';
import type { TypeOpsLogsBatch } from '@/types/ops/types.ops.log';
import type { TypeOpsServersWire } from '@/types/ops/types.ops.server';
import type { TypeRecurring } from '@/types/recurring/types.recurring';
import type { TypeTrafficLive, TypeTrafficRequestsBatch } from '@/types/traffic/types.traffic.live';
import type { TypeTask } from '@/types/work/types.work.task';
import {
    dropConversationDraft,
    dropConversationTemplate,
    writeConversationDraft,
    writeConversationTemplate,
} from '@/store/api/conversation/email/conversation.draft.cache';
import { dropRecurring, writeRecurring } from '@/store/api/recurring/recurring.cache';
import workApi from '@/store/api/work/work.api';
import { writeTasks } from '@/store/api/work/work.cache';
import { dropService, writeService } from '@/store/api/begateway/begateway.services.cache';
import { writeAccessRule } from '@/store/api/begateway/begateway.access.cache';
import { dropDiagnoseRun, writeDiagnoseRun } from '@/store/api/begateway/begateway.diagnose.cache';
import { writeKey } from '@/store/api/begateway/begateway.keys.cache';
import { dropRoute, writeRoute } from '@/store/api/begateway/begateway.routes.cache';
import { writeTrafficPulse, writeTrafficRequests } from '@/store/api/traffic/traffic.live.cache';
import { LIVE_EVENTS } from '@/function/traffic/traffic.live.constants';
import {
    writeOpsActions,
    writeOpsActivity,
    writeOpsAlerts,
    writeOpsDeploys,
    writeOpsLogs,
    writeOpsRuns,
    writeOpsServers,
} from '@/store/api/ops/ops.cache';
import { dropNotification, writeNotification } from '@/store/api/notification/notification.cache';
import { OPS_EVENTS } from '@/lib/socket/ops.events';
import { sessionRefreshed } from '@/store/auth/auth.slice';
import securityEventHandle from '@/lib/socket/security.event.handle';
import type { TypeSecurityEvent } from '@/function/auth/security.event.kicks';
import type { TypeAppDispatch } from '@/store/store';

const byName = (a: TypeCustomer, b: TypeCustomer) => a.name.localeCompare(b.name, 'it');

type TypeListEndpoint = 'listProjects' | 'listBoards' | 'listTags' | 'listTasks';

export const registerSocketHandlers = (socket: Socket, dispatch: TypeAppDispatch): void => {
    const patch = (apply: (draft: TypeCustomer[]) => void) => {
        dispatch(customerApi.util.updateQueryData('listCustomers', undefined, apply));
    };

    const detail = (customer: TypeCustomer) => {
        dispatch(customerApi.util.updateQueryData('readCustomer', customer.id, () => customer));
    };

    socket.on('me:update', (user: TypeSessionUser) => {
        dispatch(sessionRefreshed(user));
    });

    socket.on('rt:security', (event: TypeSecurityEvent) => securityEventHandle(dispatch, event));

    socket.on('company:update', (company: TypeCompany) => writeCompany(dispatch, company));

    socket.on('mail:insert', (mail: TypeMail) => writeMail(dispatch, mail));
    socket.on('mail:update', (mail: TypeMail) => writeMail(dispatch, mail));

    socket.on('conversations:insert', (conversation: TypeConversation) => writeConversation(dispatch, conversation));
    socket.on('conversations:update', (conversation: TypeConversation) => writeConversation(dispatch, conversation));
    socket.on('conversations:replace', (conversation: TypeConversation) => writeConversation(dispatch, conversation));
    socket.on('conversations:delete', (ref: { id: string }) => { if (ref?.id) dropConversation(dispatch, ref.id); });
    socket.on('conversation-messages:insert', (message: TypeConversationMessage) => writeConversationMessage(dispatch, message));
    socket.on('conversation-messages:update', (message: TypeConversationMessage) => writeConversationMessage(dispatch, message));
    socket.on('conversation-messages:replace', (message: TypeConversationMessage) => writeConversationMessage(dispatch, message));
    socket.on('conversation-messages:delete', (ref: { id: string; conversationId: string }) => { if (ref?.id && ref.conversationId) dropConversationMessage(dispatch, ref); });
    socket.on('conversation-contacts:insert', (contact: TypeConversationContact) => writeConversationContact(dispatch, contact));
    socket.on('conversation-contacts:update', (contact: TypeConversationContact) => writeConversationContact(dispatch, contact));
    socket.on('conversation-contacts:replace', (contact: TypeConversationContact) => writeConversationContact(dispatch, contact));
    socket.on('chat-messages:upsert', (message: TypeChatMessage) => {
        if (!message?.id || !message.channelId) return;
        chatTypingStop(message.channelId, message.authorId);
        writeChatMessage(dispatch, message);
        syncChatPinned(dispatch, message);
        emitChatIncoming(message);
    });
    socket.on('chat-messages:delete', (ref: { id: string; channelId: string; reason?: 'deleted' | 'expired' }) => {
        if (!ref?.id || !ref.channelId) return;
        dropChatMessage(dispatch, { id: ref.id, channelId: ref.channelId, reason: ref.reason === 'expired' ? 'expired' : 'deleted' });
        dropChatPinned(dispatch, { id: ref.id, channelId: ref.channelId });
    });
    socket.on('chat-presence:update', (update: TypeChatPresenceUpdate) => {
        if (!update?.userId || !update.presence) return;
        applyChatPresence(dispatch, { userId: update.userId, presence: update.presence, lastSeenAt: update.lastSeenAt ?? null });
    });
    socket.on('chat-reads:update', (read: { channelId: string; userId: string; lastReadAt: string }) => { if (read?.channelId && read.userId) applyChatRead(dispatch, read); });
    socket.on('chat-channels:upsert', (event: TypeChatChannelUpsert) => {
        if (event?.channel?.id) upsertChatChannel(dispatch, event.channel, event.members ?? []);
    });
    socket.on('chat-mute:update', (state: { channelId: string; mutedUntil: string | null }) => { if (state?.channelId) writeChatMute(dispatch, { channelId: state.channelId, mutedUntil: state.mutedUntil ?? null }); });
    socket.on('chat-archive:update', (state: { channelId: string; archivedAt: string | null }) => { if (state?.channelId) writeChatArchive(dispatch, { channelId: state.channelId, archivedAt: state.archivedAt ?? null }); });
    socket.on('chat-channels:removed', (event: { id: string }) => { if (event?.id) removeChatChannel(dispatch, event.id); });
    socket.on('chat:typing', (event: { channelId: string; userId: string }) => { if (event?.channelId && event.userId) chatTypingSeen(event.channelId, event.userId); });
    socket.on('conversation-drafts:upsert', (draft: TypeConversationDraft) => writeConversationDraft(dispatch, draft));
    socket.on('conversation-drafts:delete', (ref: { id: string }) => { if (ref?.id) dropConversationDraft(dispatch, ref.id); });
    socket.on('conversation-templates:upsert', (template: TypeConversationTemplate) => writeConversationTemplate(dispatch, template));
    socket.on('conversation-templates:delete', (ref: { id: string }) => { if (ref?.id) dropConversationTemplate(dispatch, ref.id); });
    socket.on('conversation-contacts:delete', (ref: { id: string }) => { if (ref?.id) dropConversationContact(dispatch, ref.id); });

    socket.on('recurrings:insert', (recurring: TypeRecurring) => writeRecurring(dispatch, recurring));
    socket.on('recurrings:update', (recurring: TypeRecurring) => writeRecurring(dispatch, recurring));
    socket.on('recurrings:delete', ({ id }: { id: string }) => dropRecurring(dispatch, id));
    socket.on('company:delete', () => {
        dispatch(companyApi.util.invalidateTags(['Company']));
    });

    socket.on('customers:insert', (customer: TypeCustomer) => {
        patch((draft) => {
            if (draft.some((entry) => entry.id === customer.id)) return;
            draft.push(customer);
            draft.sort(byName);
        });
        detail(customer);
    });

    socket.on('customers:update', (customer: TypeCustomer) => {
        patch((draft) => {
            const index = draft.findIndex((entry) => entry.id === customer.id);
            if (index >= 0) draft[index] = customer; else draft.push(customer);
            draft.sort(byName);
        });
        detail(customer);
    });

    socket.on('customers:delete', ({ id }: { id: string }) => patch((draft) => {
        const index = draft.findIndex((entry) => entry.id === id);
        if (index >= 0) draft.splice(index, 1);
    }));

    const quotes = (apply: (draft: TypeQuote[]) => void) => {
        dispatch(quoteApi.util.updateQueryData('listQuotes', undefined, apply));
    };

    const writeQuote = (quote: TypeQuote) => {
        quotes((draft) => {
            const index = draft.findIndex((entry) => entry.id === quote.id);
            if (index >= 0) draft[index] = quote; else draft.push(quote);
            draft.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
        });
        dispatch(quoteApi.util.updateQueryData('readQuote', quote.id, () => quote));
    };

    socket.on('catalogitems:insert', (item: TypeCatalogItem) => writeItem(dispatch, item));
    socket.on('catalogitems:update', (item: TypeCatalogItem) => writeItem(dispatch, item));
    socket.on('catalogitems:delete', ({ id }: { id: string }) => {
        dropItem(dispatch, id);
        dispatch(catalogApi.util.invalidateTags([{ type: 'CatalogItem', id }]));
    });

    socket.on('catalogbundles:insert', (bundle: TypeCatalogBundle) => writeBundle(dispatch, bundle));
    socket.on('catalogbundles:update', (bundle: TypeCatalogBundle) => writeBundle(dispatch, bundle));
    socket.on('catalogbundles:delete', ({ id }: { id: string }) => {
        dropBundle(dispatch, id);
        dispatch(catalogApi.util.invalidateTags([{ type: 'CatalogBundle', id }]));
    });

    socket.on('quotes:insert', writeQuote);
    socket.on('quotes:update', writeQuote);
    socket.on('quotes:delete', ({ id }: { id: string }) => {
        quotes((draft) => {
            const index = draft.findIndex((entry) => entry.id === id);
            if (index >= 0) draft.splice(index, 1);
        });

        dispatch(quoteApi.util.invalidateTags([{ type: 'Quote', id }]));
    });

    const invoices = (apply: (draft: TypeInvoice[]) => void) => {
        dispatch(invoiceApi.util.updateQueryData('listInvoices', undefined, apply));
    };

    const writeInvoice = (invoice: TypeInvoice) => {
        invoices((draft) => {
            const index = draft.findIndex((entry) => entry.id === invoice.id);
            if (index >= 0) draft[index] = invoice; else draft.push(invoice);
            draft.sort((a, b) => b.issuedAt.localeCompare(a.issuedAt) || b.number.seq - a.number.seq);
        });
        dispatch(invoiceApi.util.updateQueryData('readInvoice', invoice.id, () => invoice));
    };

    socket.on('invoices:insert', writeInvoice);
    socket.on('invoices:update', writeInvoice);
    socket.on('invoices:delete', ({ id }: { id: string }) => {
        invoices((draft) => {
            const index = draft.findIndex((entry) => entry.id === id);
            if (index >= 0) draft.splice(index, 1);
        });

        dispatch(invoiceApi.util.invalidateTags([{ type: 'Invoice', id }]));
    });

    socket.on('expenses:insert', (expense: TypeExpense) => writeExpense(dispatch, expense));
    socket.on('expenses:update', (expense: TypeExpense) => writeExpense(dispatch, expense));
    socket.on('expenses:delete', ({ id }: { id: string }) => {
        dropExpense(dispatch, id);
        dispatch(expenseApi.util.invalidateTags([{ type: 'Expense', id }]));
    });

    const listen = (entity: string, endpoint: TypeListEndpoint) => {
        const write = (entry: { id: string }) => {
            if (endpoint === 'listTasks') { writeTasks(dispatch, [entry as TypeTask]); return; }
            dispatch(workApi.util.updateQueryData(endpoint, undefined, (draft: { id: string }[]) => {
                const index = draft.findIndex((item) => item.id === entry.id);
                if (index >= 0) draft[index] = entry as any; else draft.push(entry as any);
            }));
        };

        socket.on(`${entity}:insert`, write);
        socket.on(`${entity}:update`, write);
        socket.on(`${entity}:delete`, ({ id }: { id: string }) => {
            dispatch(workApi.util.updateQueryData(endpoint, undefined, (draft: { id: string }[]) => {
                const index = draft.findIndex((item) => item.id === id);
                if (index >= 0) draft.splice(index, 1);
            }));
        });
    };

    listen('projects', 'listProjects');
    listen('boards', 'listBoards');
    listen('tags', 'listTags');
    listen('tasks', 'listTasks');

    const writeCalendarEvent = (event: TypeEvent) => writeEvent(dispatch, event);

    socket.on('events:insert', writeCalendarEvent);
    socket.on('events:update', writeCalendarEvent);
    socket.on('daynotes:update', (note: TypeDayNote) => writeDayNote(dispatch, note));
    socket.on('events:delete', ({ id }: { id: string }) => dropEvent(dispatch, id));
    socket.on('externalCalendars:update', (calendar: TypeExternalCalendar) => writeExternalCalendar(dispatch, calendar));
    socket.on('externalCalendars:delete', ({ id }: { id: string }) => dropExternalCalendar(dispatch, id));

    const auditChanged = (target: string) => dispatch(dashboardApi.util.invalidateTags([{ type: 'GatewayAudit', id: target }]));

    socket.on('gatewayservices:insert', (service: TypeGatewayService) => { writeService(dispatch, service); auditChanged(service.serviceId); });
    socket.on('gatewayservices:update', (service: TypeGatewayService) => { writeService(dispatch, service); auditChanged(service.serviceId); });
    socket.on('gatewayservices:delete', ({ serviceId }: { serviceId: string }) => dropService(dispatch, serviceId));

    socket.on('gatewaykeys:insert', (apiKey: TypeApiKey) => { writeKey(dispatch, apiKey); auditChanged(apiKey.id); });
    socket.on('gatewaykeys:update', (apiKey: TypeApiKey) => { writeKey(dispatch, apiKey); auditChanged(apiKey.id); });

    socket.on('gatewayaccess:insert', (rule: TypeAccessRule) => { writeAccessRule(dispatch, rule); auditChanged(rule.id); });
    socket.on('gatewayaccess:update', (rule: TypeAccessRule) => { writeAccessRule(dispatch, rule); auditChanged(rule.id); });

    socket.on('gatewaydiagnose:insert', (run: TypeDiagnoseRun) => writeDiagnoseRun(dispatch, run));
    socket.on('gatewaydiagnose:delete', (ref: { id: string }) => { if (ref?.id) dropDiagnoseRun(dispatch, ref.id); });

    socket.on('gatewayendpoints:insert', (endpoint: TypeGatewayEndpoint) => writeEndpoint(dispatch, endpoint));
    socket.on('gatewayendpoints:update', (payload: TypeGatewayEndpoint | { route: string }) => {
        if ('id' in payload && payload.id) {
            writeEndpoint(dispatch, payload);
            return;
        }
        dispatch(begatewayEndpointsApi.util.invalidateTags([{ type: 'GatewayEndpoint', id: payload.route }, { type: 'GatewayEndpoint', id: SERVICE_ENDPOINTS_TAG }]));
    });
    socket.on('gatewayendpoints:delete', (ref: { id: string; route: string }) => dropEndpoint(dispatch, { ...ref, serviceId: null }));

    socket.on('gatewayroutes:insert', (route: TypeRoute) => writeRoute(dispatch, route));
    socket.on('gatewayroutes:update', (route: TypeRoute) => writeRoute(dispatch, route));
    socket.on('gatewayroutes:delete', (ref: { kind: string; name: string }) => dropRoute(dispatch, ref));

    socket.on(LIVE_EVENTS.REQUESTS, (batch: TypeTrafficRequestsBatch) => writeTrafficRequests(dispatch, batch));
    socket.on(LIVE_EVENTS.PULSE, (live: TypeTrafficLive) => writeTrafficPulse(dispatch, live));
    socket.on(OPS_EVENTS.SERVERS, (wire: TypeOpsServersWire) => writeOpsServers(dispatch, wire));
    socket.on(OPS_EVENTS.ACTIVITY, (wire: { items?: TypeOpsActivity[] }) => writeOpsActivity(dispatch, wire));
    socket.on(OPS_EVENTS.LOGS, (batch: TypeOpsLogsBatch) => writeOpsLogs(dispatch, batch));
    socket.on(OPS_EVENTS.ACTIONS, (wire: { runs?: TypeOpsActionRun[] }) => writeOpsActions(dispatch, wire));
    socket.on(OPS_EVENTS.RUNS, (wire: { runs?: TypeOpsRun[] }) => writeOpsRuns(dispatch, wire));
    socket.on(OPS_EVENTS.ALERTS, (wire: { alerts?: TypeOpsAlert[] }) => writeOpsAlerts(dispatch, wire));
    socket.on(OPS_EVENTS.DEPLOYS, (wire: { deploys?: TypeOpsDeploy[] }) => writeOpsDeploys(dispatch, wire));

    socket.on('notifications:upsert', (notification: TypeNotification) => writeNotification(dispatch, notification));
    socket.on('notifications:delete', (wire: { id?: string }) => { if (typeof wire?.id === 'string') dropNotification(dispatch, wire.id); });
};

export default registerSocketHandlers;
